import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { withRouter } from '../../common/withRouter';
import { Notification } from "../../web.common";
import { LayoutContext } from "../layout/siteLayout";
import PageTop from "../layout/PageTop";
import withApi from "../higherOrderComponents/withApi";
import { Filters, FilterSelectAsync, FilterSelect } from "./Filters";
import { getQueryStringParameters } from "../../common/utils";
import AddEntityButton from "../misc/AddEntityButton";
import { LANGUAGE_OPTIONS } from "./seoOptions";
import FacilityTextsTable from "./FacilityTextsTable";

/**
 * Facility texts — the editorial copy of one facility in one language: the text shown on the
 * page, the SEO title, the meta description and the H1.
 *
 * Same shape as SEO Texts, which does the job for the area/sport pages: one list for everybody
 * who edits copy, so working through several facilities does not mean opening a facility screen
 * for each one.
 */

const FacilityTextsInternal = (props) => {
  const { location, history } = props;
  const { facilities: { lookupByNameOrId: lookupFacility } } = props.api;
  const [loadedData, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!loadedData) {
      loadData();
    }
  });

  const loadData = async () => {
    setIsLoading(false);
    const filters = getQueryStringParameters(location.search);
    try {
      const data = await props.api.facilityContent.searchFacilityTexts({ filters });
      setData(Array.isArray(data) ? data : []);
      setIsLoading(true);
    } catch (e) {
      console.error('Error loading facility texts:', e);
      setData([]);
      setIsLoading(true);
    }
  };

  return (
    <LayoutContext.Consumer>
      {() => (
        <React.Fragment>
          <Notification newMessage={null} />
          <PageTop
            documentTitle="Facility Texts"
            documentDescription="Manage per-facility/language page text and SEO meta"
          />

          <h2 className="main-page-header">
            Facility Texts
            <AddEntityButton to='/facility-text' />
          </h2>

          <Filters
            initFromUrl
            submitUrl
            validateForm={({ facilityId, language }) => ({ facilityId, language })}
            render={(formData, update) => (
              <div className="filters-block">
                <div className="filters-label-block">
                  <label className="filters-label" htmlFor="facilityId">Facility</label>
                  <FilterSelectAsync
                    className="filter-select"
                    data={formData}
                    debounce
                    update={update}
                    name="facilityId"
                    loadOptions={async (inputValue) => lookupFacility(inputValue)}
                  />
                </div>
                <div className="filters-label-block">
                  <label className="filters-label" htmlFor="language">Language</label>
                  <FilterSelect className='filter-select' data={formData} update={update} name='language' options={LANGUAGE_OPTIONS} />
                </div>
                <br />
                <br />
                <div className="filters-submit-wrapper">
                  <input type="submit" value="Search" disabled={!isLoading} />
                </div>
                <hr />
              </div>
            )}
          />

          {loadedData == null ? (
            <span>Loading...</span>
          ) : loadedData.length > 0 ? (
            <FacilityTextsTable
              rows={loadedData}
              onRowClick={(row) => history.push(`/facility-text/${row.id}`)}
            />
          ) : (
            <div style={{ padding: "20px", textAlign: "center", color: "grey" }}>
              No facility texts found matching your search criteria.
            </div>
          )}
        </React.Fragment>
      )}
    </LayoutContext.Consumer>
  );
};

FacilityTextsInternal.propTypes = {
  api: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  isLoggedIn: PropTypes.bool,
};

const FacilityTexts = withRouter(
  withApi(
    connect(({ user }) => ({ isLoggedIn: !!user }), {})(FacilityTextsInternal)
  )
);

FacilityTexts.requiresLogin = true;

export default FacilityTexts;
